import { BaseService } from './core/BaseService';
import { apiClient } from './core/ApiClient';

class AnalyticsService extends BaseService {
  constructor() {
    super('/analytics');
  }

  // Date-wise ticket analytics
  async getDateWiseTickets(params = {}) {
    return this.customEndpoint('date-wise', 'GET', null, params);
  }

  // Status-wise ticket analytics
  async getStatusWiseTickets(params = {}) {
    return this.customEndpoint('status-wise', 'GET', null, params);
  }

  // Priority-wise ticket analytics
  async getPriorityWiseTickets(params = {}) {
    return this.customEndpoint('priority-wise', 'GET', null, params);
  }

  // User-wise ticket analytics
  async getUserWiseTickets(params = {}) {
    return this.customEndpoint('user-wise', 'GET', null, params);
  }

  // Module-wise ticket analytics
  async getModuleWiseTickets(params = {}) {
    return this.customEndpoint('module-wise', 'GET', null, params);
  }

  // Category-wise ticket analytics
  async getCategoryWiseTickets(params = {}) {
    return this.customEndpoint('category-wise', 'GET', null, params);
  }

  // Branch-wise ticket analytics
  async getBranchWiseTickets(params = {}) {
    return this.customEndpoint('branch-wise', 'GET', null, params);
  }

  // Resolution time analytics
  async getResolutionTimeAnalytics(params = {}) {
    return this.customEndpoint('resolution-time', 'GET', null, params);
  }

  // First response time analytics
  async getFirstResponseTimeAnalytics(params = {}) {
    return this.customEndpoint('first-response-time', 'GET', null, params);
  }

  // Customer satisfaction analytics
  async getCustomerSatisfactionAnalytics(params = {}) {
    return this.customEndpoint('customer-satisfaction', 'GET', null, params);
  }

  // Workload analytics
  async getWorkloadAnalytics(params = {}) {
    return this.customEndpoint('workload', 'GET', null, params);
  }

  // Trend analytics
  async getTrendAnalytics(params = {}) {
    const { period = '30d', metric = 'tickets' } = params;
    return this.customEndpoint('trends', 'GET', null, { period, metric, ...params });
  }

  // Performance metrics
  async getPerformanceMetrics(params = {}) {
    return this.customEndpoint('performance', 'GET', null, params);
  }

  // SLA compliance analytics
  async getSLACompliance(params = {}) {
    return this.customEndpoint('sla-compliance', 'GET', null, params);
  }

  // Team performance analytics
  async getTeamPerformance(params = {}) {
    return this.customEndpoint('team-performance', 'GET', null, params);
  }

  // Ticket volume predictions
  async getVolumePreductions(params = {}) {
    return this.customEndpoint('volume-predictions', 'GET', null, params);
  }

  // Custom analytics query
  async runCustomQuery(queryParams) {
    return this.customEndpoint('custom-query', 'POST', queryParams);
  }

  // Export analytics data
  async exportAnalytics(type, params = {}, format = 'csv') {
    const queryParams = { ...params, format };
    return apiClient.download(
      `${this.baseEndpoint}/${type}/export?${apiClient.buildQuery(queryParams)}`,
      `analytics_${type}_${Date.now()}.${format}`
    );
  }

  // Dashboard summary
  async getDashboardSummary(params = {}) {
    return this.customEndpoint('dashboard-summary', 'GET', null, params);
  }

  // Real-time metrics
  async getRealTimeMetrics() {
    return this.customEndpoint('real-time', 'GET');
  }

  // Comparative analysis
  async getComparativeAnalysis(params = {}) {
    return this.customEndpoint('comparative', 'GET', null, params);
  }

  // Anomaly detection
  async getAnomalies(params = {}) {
    return this.customEndpoint('anomalies', 'GET', null, params);
  }

  // Heat map data
  async getHeatMapData(params = {}) {
    return this.customEndpoint('heatmap', 'GET', null, params);
  }

  // Forecast data
  async getForecastData(params = {}) {
    return this.customEndpoint('forecast', 'GET', null, params);
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export class for testing or custom instances
export { AnalyticsService };